<script>
  import { onMount } from 'svelte';
  import { Link, link, navigate, Route, Router } from 'svelte-routing';
  import { channel as chatChannel, Chat } from 'tv2no-chat';
  import {
    getCategories,
    getIdTokenResult,
    logout,
    onAuthStateChanged,
    getUserChatId,
  } from './Api/api';
  import Channels from './Components/Chat/Channels/Channels.svelte';
  import Login from './Components/Login.svelte';
  import CompactModerator from './Components/Chat/Moderator/CompactModerator.svelte';
  import { applicationConfig, categories, firebaseApp, singleChatId, user } from './Store/store';
  import { getFirebaseApp } from './Api/firebase';
  import Messages from './Components/Messages/Messages.svelte';
  import { getApplicationConfig, getHlsUrls } from './Api/videoApi';
  import AppHeader from './AppHeader.svelte';
  import Published from './Components/Chat/Published/Published.svelte';
  import Broadcast from './Components/Chat/Broadcast/Broadcast.svelte';
  import ReporterHeader from './ReporterHeader.svelte';
  import PlaylistContainer from './Components/Playlists/VideoPlaylistContainer.svelte';
  import ChatAdmin from './Routes/ChatAdmin.svelte';
  import { videoPlayer, broadcaster } from './Components/Chat/Broadcast/broadcast-store';
  import { stream as rtcStream } from './Components/Chat/Broadcast/CanvasRenderer/store';
  import Canvas from './Components/Chat/Broadcast/CanvasRenderer/Canvas.svelte';
  import Video from './Components/Chat/Broadcast/CanvasRenderer/Video.svelte';
  import VideoBrowser from './Components/Chat/VideoBrowser/VideoBrowser.svelte';
  import { calcVideoDrawSize } from './Components/Chat/Broadcast/CanvasRenderer/video-utils';
  import Profiles from './Components/Profiles/Profiles.svelte';
  import { EmbedChat } from './Elements/EmbedChat';

  $: $broadcaster.videoCanPlay && enableVideoChat();
  $: !$broadcaster.videoCanPlay && (loadCanvas = false);

  $: console.log(loadCanvas);

  let hlsURL;
  let loadCanvas = false;
  let appVideoContainer;

  const initConfig = async () => {
    const config = await getApplicationConfig();
    applicationConfig.set(config);
  };

  const getFirebaseConfig = async () => {
    const app = await getFirebaseApp();
    if (app) {
      firebaseApp.set(app);
    }
  };

  const onChannelClickedReporter = (e) => {
    navigate(`/reporter/${e.detail.channel.id}`);
  };

  const initHlsUrls = async () => {
    const urls = await getHlsUrls();
    hlsURL = urls.hlsURL;
  };

  onMount(async () => {
    await getFirebaseConfig();
    await initConfig();
    await initHlsUrls();

    const channelCategories = await getCategories();
    const allCategories = [
      { type: 'all', displayName: 'Alle', sortOrder: 0 },
      ...channelCategories,
    ].sort((a, b) => a.sortOrder - b.sortOrder);
    categories.set(allCategories);

    await onAuthStateChanged(async (loggedInUser) => {
      if (!loggedInUser) {
        user.set(null);
      } else {
        getIdTokenResult().then(async (token) => {
          if (token) {
            if (['admin', 'super-admin'].includes(token.claims.level)) {
              user.set(loggedInUser);
            } else if (token.claims.level === 'singleChat') {
              let chatId = await getUserChatId(loggedInUser.uid);
              singleChatId.set(chatId);
              user.set(loggedInUser);
              navigate(`/chat/edit/${chatId}`);
            }
          }
        });
      }
    });
  });

  const onCanvasReady = () => {
    console.log('onCanvasReady');
    broadcaster.setupAudio($rtcStream, $broadcaster.userMediaStream);
    broadcaster.setStream($rtcStream);
  };

  const onTabClicked = (e) => {
    // This is for when the user changes away from video tab when doing live video chat
    // We need the player to be able to draw on canvas generating the stream.
    if (e.detail !== 'video' && $videoPlayer) {
      appVideoContainer.appendChild($videoPlayer);
      $broadcaster.guestStreamInfos.forEach((gsi) => appVideoContainer.appendChild(gsi.video));
    }
  };

  const enableVideoChat = async () => {
    loadCanvas = true;
  };

  let addedGuestStreamIds = [];
  let addedGuestStreams = {};

  $: $broadcaster.guestStreams && addGuestPreview();

  const addGuestPreview = async () => {
    if (
      !$broadcaster.streamingPreviewToGuests &&
      $broadcaster.guestStreams.length &&
      !$broadcaster.joinAsGuest
    ) {
      broadcaster.update((s) => ({ ...s, streamingPreviewToGuests: true }));
      broadcaster.streamToGuests(
        $broadcaster.roomId,
        $broadcaster.userMediaStream,
        $applicationConfig.publishURL,
        $videoPlayer,
      );
    }

    if (
      $broadcaster.streamingPreviewToGuests &&
      $broadcaster.guestStreams.length === 1 &&
      !$broadcaster.joinAsGuest
    ) {
      if ($broadcaster.guestStreams[0].streamId === $broadcaster.guestStreamId) {
        broadcaster.stopGuestStream();
      }
    }

    $broadcaster.guestStreams.forEach((s) => {
      if (!addedGuestStreamIds.includes(s.streamId) && $broadcaster.guestStreamId !== s.streamId) {
        let streamInfo = { ...s };
        if (!$broadcaster.joinAsGuest) {
          streamInfo.audioSource = $broadcaster.audioCtx.createMediaStreamSource(s.stream);
          streamInfo.audioGainNode = $broadcaster.audioCtx.createGain();
          streamInfo.audioGainNode.gain.value = 1;
          streamInfo.audioSource
            .connect(streamInfo.audioGainNode)
            .connect($broadcaster.audioDestination);
        }

        const video = document.createElement('video');
        video.autoplay = true;
        video.setAttribute('playsinline', 'playsinline');
        video.setAttribute('webkit-playsinline', 'webkit-playsinline');

        video.srcObject = s.stream;
        video.setAttribute('style', 'height:50%; border: 2px solid black;');
        video.setAttribute('streamid', s.streamId);

        streamInfo.video = video;

        video.addEventListener('click', () => {
          video.muted = !video.muted;
          if (video.muted) {
            video.style.border = '2px solid red';
          } else {
            video.style.border = '2px solid black';
          }
        });

        video.onloadedmetadata = (e) => {
          const streamId = e.target.getAttribute('streamid');
          let si = addedGuestStreams[streamId];
          si = { ...si, ...calcVideoDrawSize(si.video) };
          addedGuestStreams[streamId] = si;
          // guestContainer.appendChild(si.video);
          appVideoContainer.appendChild(si.video);
          const updated = $broadcaster.guestStreamInfos.map((gsi) => {
            if (gsi.streamId === streamId) {
              return si;
            }
            return gsi;
          });
          broadcaster.update((s) => ({ ...s, guestStreamInfos: updated }));
        };

        addedGuestStreamIds.push(s.streamId);
        addedGuestStreams[s.streamId] = streamInfo;
      }
    });

    const streamsToBeRemoved = addedGuestStreamIds.filter((sId) => {
      const found = $broadcaster.guestStreams.find((gs) => gs.streamId === sId);
      return found ? false : true;
    });

    streamsToBeRemoved.forEach((s) => {
      const streamInfo = addedGuestStreams[s];
      if (streamInfo.video) {
        streamInfo.video.remove();
      }
      addedGuestStreamIds = addedGuestStreamIds.filter((sId) => sId !== streamInfo.streamId);
      streamInfo.video = null;
      if (!$broadcaster.joinAsGuest) {
        streamInfo.audioSource.disconnect(streamInfo.audioGainNode);
        streamInfo.audioGainNode.disconnect($broadcaster.audioDestination);
        streamInfo.audioGainNode = null;
        streamInfo.audioSource = null;
      }

      delete addedGuestStreams[s];
    });

    const guestStreamInfos = addedGuestStreamIds.map((id) => addedGuestStreams[id]);
    broadcaster.update((s) => ({ ...s, guestStreamInfos }));
  };
</script>

<main>
  {#if $singleChatId}
    <Router>
      <Route path="/chat/edit/:id" let:params>
        {#if $user}
          <ChatAdmin {hlsURL} channelId={params.id} on:tabclicked={onTabClicked} />
        {:else}
          <Login />
        {/if}
      </Route>
    </Router>
  {:else}
    <Router>
      <Route path="/">
        {#if $user}
          <AppHeader />
          <div class="main-navigation">
            <nav class="items">
              <div class="item">
                <Link to={`/chat`}>
                  <span class="material-icons">chat</span>Chat
                </Link>
              </div>
              <div class="item">
                <Link to={`/reporter`}>
                  <span class="material-icons">phone_iphone</span>Mobile
                </Link>
              </div>
              <div class="item disabled">
                <Link to={`/meldinger`}>
                  <span class="material-icons">rss_feed</span>Meldinger
                </Link>
              </div>
              <div class="item disabled">
                <Link to={'/spillelister'}>
                  <span class="material-icons">video_library</span>Spillelister
                </Link>
              </div>
              <div class="item disabled">
                <Link to={`/profiler`}>
                  <span class="material-icons">person</span>Profiles
                </Link>
              </div>
            </nav>
          </div>
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/spillelister">
        {#if $user}
          <AppHeader />
          <PlaylistContainer />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/meldinger">
        {#if $user}
          <AppHeader />
          <Messages />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/profiler">
        {#if $user}
          <AppHeader />
          <Profiles />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/login">
        <Login />
      </Route>
      <Route path="/chat/published">
        <Published />
      </Route>
      <Route path="/chat">
        {#if $user}
          <ChatAdmin {hlsURL} on:tabclicked={onTabClicked} />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/chat/edit/:id" let:params>
        {#if $user}
          <ChatAdmin {hlsURL} channelId={params.id} on:tabclicked={onTabClicked} />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/reporter">
        {#if $user}
          <div class="reporter">
            <div class="reporter-container">
              <Channels on:channelclicked={onChannelClickedReporter} standAlone={true} />
            </div>
          </div>
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/reporter/:id" let:params>
        {#if $user}
          <div class="reporter">
            <ReporterHeader channelId={params.id} />
            <div class="reporter-container">
              <div class="broadcaster">
                <Broadcast channelId={params.id} />
              </div>
              <div class="moderator">
                <CompactModerator channelId={params.id} />
              </div>
            </div>
          </div>
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/chat/video">
        {#if $user}
          <VideoBrowser />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/chat/video/:key" let:params>
        {#if $user}
          <VideoBrowser selectedRecordingKey={params.key} />
        {:else}
          <Login />
        {/if}
      </Route>
      <Route path="/chat/:id" let:params>
        <div class="chat-container">
          <div class="chat">
            {#if $firebaseApp && $applicationConfig.playURL}
              <Chat
                id={params.id}
                app={$firebaseApp}
                websocketURL={$applicationConfig.playURL}
                {hlsURL}
              >
                <div class="app-nav" slot="mobile_navigation">
                  <a
                    class="nav-btn"
                    href={`/chat/published${
                      $chatChannel && $chatChannel.category ? `?type=${$chatChannel.category}` : ''
                    }`}
                    use:link
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M11 14L11 19H10L4 13V11L10 5H11L11 10H20V14H11Z" fill="#FAFAFA" />
                    </svg>
                  </a>
                  <div class="content">
                    <h1>{$chatChannel ? $chatChannel.name : 'TV2 Chat'}</h1>
                  </div>
                </div>
              </Chat>
            {/if}
          </div>
        </div>
      </Route>

      <!-- Bits -->
      <Route path="/chat/embed/:id" let:params>
        <EmbedChat {params} {hlsURL} />
      </Route>

      <Route path="/logout" let:params>
        <div class="chat-container">
          <button on:click={() => logout()}>Loggut</button>
        </div>
      </Route>
    </Router>
  {/if}
</main>

<div class="hidden-video">
  <div bind:this={appVideoContainer} />

  {#if loadCanvas}
    <Canvas on:canvasready={onCanvasReady}>
      <Video
        videoEl={$videoPlayer}
        canDrawVideo={!$broadcaster.flippingCam}
        guestStreams={$broadcaster.guestStreamInfos}
        drawGuests={!$broadcaster.joinAsGuest}
      />
    </Canvas>
  {/if}
</div>

<style src="./app.scss">
</style>
